import parseDateToUtcIso from './date';

const createField = (key, label, options = {}) => ({
  key,
  label,
  required: false,
  type: 'string',
  ...options,
});

const futureFields = [
  createField('segment', 'Segment', { required: true }),
  createField('symbolName', 'Symbol Name', { required: true }),
  createField('expiryDate', 'Expiry Date', { required: true, type: 'date' }),
  createField('quantity', 'Quantity', { required: true, type: 'integer' }),
  createField('buyDate', 'Buy Date', { required: true, type: 'date' }),
  createField('sellDate', 'Sell Date', { required: true, type: 'date' }),
  createField('avgBuyPrice', 'Avg Buy Price', { type: 'number' }),
  createField('buyValue', 'Buy Value', { type: 'number' }),
  createField('avgSellPrice', 'Avg Sell Price', { type: 'number' }),
  createField('sellValue', 'Sell Value', { type: 'number' }),
  createField('totalChargesAndStatutoryLevies', 'Total Charges and Statutory Levies', { type: 'number' }),
  createField('stt', 'STT', { type: 'number' }),
];

const optionFields = [
  ...futureFields.slice(0, 3),
  createField('strikePrice', 'Strike Price', { required: true, type: 'number' }),
  createField('optionType', 'Option Type', { required: true }),
  ...futureFields.slice(3),
  createField('taxablePL', 'Taxable P&L', { type: 'number' }),
  createField('turnover', 'Turnover', { type: 'number' }),
];

export const DATA_TYPE_OPTIONS = [
  { value: 'delivery', label: 'Delivery' },
  { value: 'intraday', label: 'Intraday' },
  { value: 'future', label: 'Future' },
  { value: 'option', label: 'Option' },
  { value: 'commodityfuture', label: 'Commodity Future' },
  { value: 'commodityoption', label: 'Commodity Option' },
  { value: 'capitalasset', label: 'Capital Asset / Equity MF' },
];

export const IMPORT_SOURCE_OPTIONS = [
  { value: 'angel', label: 'Angel One preset' },
  { value: 'zerodha', label: 'Zerodha preset' },
  { value: 'smc', label: 'SMC preset' },
  { value: 'custom', label: 'Custom mapping' },
];

export const BROKER_PRESET_OPTIONS = IMPORT_SOURCE_OPTIONS.filter((option) => option.value !== 'custom');

export const getImportSourceLabel = (value) => IMPORT_SOURCE_OPTIONS.find((option) => option.value === value)?.label || 'Unknown source';

const FIELD_DEFINITIONS = {
  delivery: [
    createField('instrumentId', 'Instrument ID'),
    createField('isin', 'ISIN'),
    createField('exchange', 'Exchange'),
    createField('exchangeSegment', 'Exchange Segment'),
    createField('tradingSymbol', 'Trading Symbol'),
    createField('angelToken', 'Angel Token'),
    createField('bseScripCode', 'BSE Scrip Code'),
    createField('scripName', 'Scrip Name', { required: true }),
    createField('quantity', 'Quantity', { required: true, type: 'integer' }),
    createField('buyDate', 'Buy Date', { required: true, type: 'date' }),
    createField('sellDate', 'Sell Date', { required: true, type: 'date' }),
    createField('avgBuyPrice', 'Avg Buy Price', { type: 'number' }),
    createField('buyValue', 'Buy Value', { type: 'number' }),
    createField('avgSellPrice', 'Avg Sell Price', { type: 'number' }),
    createField('sellValue', 'Sell Value', { type: 'number' }),
    createField('costOfAcquisition', 'Cost Of Acquisition', { type: 'number' }),
    createField('chargesAndStatutoryLevies', 'Charges and Statutory Levies', { type: 'number' }),
    createField('stt', 'STT', { type: 'number' }),
    createField('netProfitLoss', 'Net Profit/Loss', { type: 'number' }),
    createField('longTermTaxableIncome', 'Long term taxable income', { type: 'number' }),
    createField('shortTermTaxableIncome', 'Short term taxable income', { type: 'number' }),
    createField('tradeClassification', 'Trade Classification'),
    createField('purchaseType', 'Purchase Type'),
    createField('typeOfInstrument', 'Type of instrument'),
    createField('turnover', 'Turnover', { type: 'number' }),
  ],
  intraday: [
    createField('isin', 'ISIN'),
    createField('scripName', 'Scrip Name', { required: true }),
    createField('quantity', 'Quantity', { required: true, type: 'integer' }),
    createField('transactionDate', 'Transaction Date', { required: true, type: 'date' }),
    createField('avgBuyPrice', 'Avg Buy Price', { type: 'number' }),
    createField('buyValue', 'Buy Value', { type: 'number' }),
    createField('avgSellPrice', 'Avg Sell Price', { type: 'number' }),
    createField('sellValue', 'Sell Value', { type: 'number' }),
    createField('chargesAndStatutoryLevies', 'Charges and Statutory Levies', { type: 'number' }),
    createField('stt', 'STT', { type: 'number' }),
    createField('taxablePL', 'Taxable P&L', { type: 'number' }),
  ],
  future: futureFields,
  option: optionFields,
  commodityfuture: futureFields,
  commodityoption: optionFields,
  capitalasset: [
    createField('assetName', 'Asset Name', { required: true }),
    createField('assetClass', 'Asset Class', { required: true }),
    createField('acquisitionDate', 'Acquisition Date', { required: true, type: 'date' }),
    createField('transferDate', 'Transfer Date', { required: true, type: 'date' }),
    createField('quantity', 'Quantity', { type: 'number' }),
    createField('saleConsideration', 'Sale Consideration', { type: 'number' }),
    createField('acquisitionCost', 'Acquisition Cost', { type: 'number' }),
    createField('transferExpenses', 'Transfer Expenses', { type: 'number' }),
    createField('costOfImprovement', 'Cost Of Improvement', { type: 'number' }),
    createField('fairMarketValueAsOn31Jan2018', 'Fair Market Value as on 31-01-2018', { type: 'number' }),
    createField('listed', 'Listed', { type: 'boolean' }),
    createField('sttApplicable', 'STT Applicable', { type: 'boolean' }),
    createField('acquisitionMode', 'Acquisition Mode'),
    createField('notes', 'Notes'),
  ],
};

const COMMON_FIELD_ALIASES = {
  delivery: {
    exchange: ['Exch', 'Exchange Name'],
    exchangeSegment: ['Segment', 'Series'],
    tradingSymbol: ['Symbol', 'Trading Symbol', 'Tradingsymbol'],
    angelToken: ['Token', 'Angel Token', 'Symbol Token'],
    bseScripCode: ['Scrip Code', 'Security Code', 'BSE Code'],
    scripName: ['Symbol', 'Trading Symbol', 'Tradingsymbol', 'Instrument'],
    quantity: ['Qty', 'Net Qty'],
    buyDate: ['Purchase Date', 'Open Date'],
    sellDate: ['Sale Date', 'Close Date'],
    avgBuyPrice: ['Average Buy Price', 'Buy Avg Price', 'Buy Price'],
    buyValue: ['Buy Amount', 'Purchase Value'],
    avgSellPrice: ['Average Sell Price', 'Sell Avg Price', 'Sell Price'],
    sellValue: ['Sell Amount', 'Sale Value'],
    chargesAndStatutoryLevies: ['Charges', 'Brokerage + Charges', 'Total Charges'],
    stt: ['STT/CTT'],
    netProfitLoss: ['P&L', 'Net P&L', 'Net Profit Loss'],
    tradeClassification: ['Classification'],
    typeOfInstrument: ['Instrument Type'],
  },
  intraday: {
    scripName: ['Symbol', 'Trading Symbol', 'Tradingsymbol', 'Instrument'],
    quantity: ['Qty', 'Net Qty'],
    transactionDate: ['Trade Date', 'Execution Date'],
    avgBuyPrice: ['Average Buy Price', 'Buy Avg Price', 'Buy Price'],
    buyValue: ['Buy Amount'],
    avgSellPrice: ['Average Sell Price', 'Sell Avg Price', 'Sell Price'],
    sellValue: ['Sell Amount'],
    chargesAndStatutoryLevies: ['Charges', 'Brokerage + Charges', 'Total Charges'],
    stt: ['STT/CTT'],
    taxablePL: ['P&L', 'Net P&L', 'Realised P&L', 'Realized P&L'],
  },
  future: {
    symbolName: ['Symbol', 'Trading Symbol', 'Tradingsymbol', 'Instrument'],
    expiryDate: ['Expiry', 'Expiry Dt'],
    quantity: ['Qty', 'Lot Quantity'],
    buyDate: ['Purchase Date', 'Open Date'],
    sellDate: ['Sale Date', 'Close Date'],
    avgBuyPrice: ['Average Buy Price', 'Buy Avg Price', 'Buy Price'],
    buyValue: ['Buy Amount'],
    avgSellPrice: ['Average Sell Price', 'Sell Avg Price', 'Sell Price'],
    sellValue: ['Sell Amount'],
    totalChargesAndStatutoryLevies: ['Charges', 'Brokerage + Charges', 'Total Charges'],
    stt: ['STT/CTT'],
  },
  option: {
    symbolName: ['Symbol', 'Trading Symbol', 'Tradingsymbol', 'Instrument'],
    expiryDate: ['Expiry', 'Expiry Dt'],
    strikePrice: ['Strike'],
    optionType: ['Option'],
    quantity: ['Qty', 'Lot Quantity'],
    buyDate: ['Purchase Date', 'Open Date'],
    sellDate: ['Sale Date', 'Close Date'],
    avgBuyPrice: ['Average Buy Price', 'Buy Avg Price', 'Buy Price'],
    buyValue: ['Buy Amount'],
    avgSellPrice: ['Average Sell Price', 'Sell Avg Price', 'Sell Price'],
    sellValue: ['Sell Amount'],
    totalChargesAndStatutoryLevies: ['Charges', 'Brokerage + Charges', 'Total Charges'],
    stt: ['STT/CTT'],
    taxablePL: ['P&L', 'Net P&L', 'Realised P&L', 'Realized P&L'],
  },
  commodityfuture: {
    symbolName: ['Symbol', 'Trading Symbol', 'Tradingsymbol', 'Instrument'],
    expiryDate: ['Expiry', 'Expiry Dt'],
    quantity: ['Qty', 'Lot Quantity'],
    buyDate: ['Purchase Date', 'Open Date'],
    sellDate: ['Sale Date', 'Close Date'],
    avgBuyPrice: ['Average Buy Price', 'Buy Avg Price', 'Buy Price'],
    buyValue: ['Buy Amount'],
    avgSellPrice: ['Average Sell Price', 'Sell Avg Price', 'Sell Price'],
    sellValue: ['Sell Amount'],
    totalChargesAndStatutoryLevies: ['Charges', 'Brokerage + Charges', 'Total Charges'],
    stt: ['STT/CTT'],
  },
  commodityoption: {
    symbolName: ['Symbol', 'Trading Symbol', 'Tradingsymbol', 'Instrument'],
    expiryDate: ['Expiry', 'Expiry Dt'],
    strikePrice: ['Strike'],
    optionType: ['Option'],
    quantity: ['Qty', 'Lot Quantity'],
    buyDate: ['Purchase Date', 'Open Date'],
    sellDate: ['Sale Date', 'Close Date'],
    avgBuyPrice: ['Average Buy Price', 'Buy Avg Price', 'Buy Price'],
    buyValue: ['Buy Amount'],
    avgSellPrice: ['Average Sell Price', 'Sell Avg Price', 'Sell Price'],
    sellValue: ['Sell Amount'],
    totalChargesAndStatutoryLevies: ['Charges', 'Brokerage + Charges', 'Total Charges'],
    stt: ['STT/CTT'],
    taxablePL: ['P&L', 'Net P&L', 'Realised P&L', 'Realized P&L'],
  },
  capitalasset: {
    assetName: ['Security Name', 'Symbol', 'Tradingsymbol', 'Trading Symbol'],
    assetClass: ['Classification', 'Asset Type'],
    acquisitionDate: ['Purchase Date'],
    transferDate: ['Sale Date'],
    saleConsideration: ['Sale Value', 'Net Sale Consideration'],
    acquisitionCost: ['Purchase Cost', 'Cost'],
    transferExpenses: ['Transfer Charges', 'Sale Expenses'],
    listed: ['Is Listed'],
    sttApplicable: ['STT Paid'],
  },
};

const SOURCE_FIELD_ALIASES = {
  angel: {
    delivery: {
      turnover: ['Tunover', 'Turnover'],
    },
  },
  zerodha: {
    delivery: {
      buyDate: ['Purchase Date', 'Opening Date'],
      sellDate: ['Sale Date', 'Closing Date'],
      netProfitLoss: ['P&L', 'Net P&L'],
    },
    intraday: {
      transactionDate: ['Trade Date'],
      taxablePL: ['P&L', 'Net P&L'],
    },
    option: {
      symbolName: ['Trading Symbol'],
      taxablePL: ['P&L', 'Net P&L'],
    },
    future: {
      symbolName: ['Trading Symbol'],
    },
    commodityoption: {
      symbolName: ['Trading Symbol'],
      taxablePL: ['P&L', 'Net P&L'],
    },
    commodityfuture: {
      symbolName: ['Trading Symbol'],
    },
    capitalasset: {
      assetName: ['Security Name', 'Trading Symbol'],
    },
  },
};

const normalizeComparable = (value) => String(value ?? '')
  .trim()
  .toLowerCase()
  .replace(/[^a-z0-9]/g, '');

const sanitizeCell = (value) => (typeof value === 'string' ? value.trim() : value);

const isBlank = (value) => value == null || (typeof value === 'string' && value.trim() === '');

const parseNumberValue = (value) => {
  if (isBlank(value)) {
    return null;
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  const raw = String(value).trim().replace(/,/g, '').replace(/[₹$]/g, '');
  const negativeMatch = raw.match(/^\((.*)\)$/);
  const candidate = negativeMatch ? `-${negativeMatch[1]}` : raw;
  const parsed = Number(candidate);
  return Number.isFinite(parsed) ? parsed : null;
};

const parseBooleanValue = (value) => {
  if (value === true || value === false) {
    return value;
  }

  if (isBlank(value)) {
    return null;
  }

  const normalized = String(value).trim().toLowerCase();

  if (['true', 'yes', 'y', '1', 'listed', 'applicable', 'paid'].includes(normalized)) {
    return true;
  }

  if (['false', 'no', 'n', '0', 'unlisted', 'not applicable', 'not paid'].includes(normalized)) {
    return false;
  }

  return null;
};

const transformValue = (type, value) => {
  switch (type) {
    case 'date':
      return parseDateToUtcIso(value);
    case 'integer': {
      const parsed = parseNumberValue(value);
      return parsed == null ? null : Math.trunc(parsed);
    }
    case 'number':
      return parseNumberValue(value);
    case 'boolean':
      return parseBooleanValue(value);
    default:
      return isBlank(value) ? null : sanitizeCell(value);
  }
};

const getAliasCandidates = (dataType, importSource, field) => {
  const commonAliases = COMMON_FIELD_ALIASES[dataType]?.[field.key] || [];
  const sourceAliases = SOURCE_FIELD_ALIASES[importSource]?.[dataType]?.[field.key] || [];
  return [field.label, field.key, ...commonAliases, ...sourceAliases];
};

const findMatchingHeader = (headers, candidates) => {
  const normalizedHeaders = new Map(headers.map((header) => [normalizeComparable(header), header]));

  for (const candidate of candidates) {
    const normalizedCandidate = normalizeComparable(candidate);
    if (normalizedCandidate && normalizedHeaders.has(normalizedCandidate)) {
      return normalizedHeaders.get(normalizedCandidate);
    }
  }

  return '';
};

export const getImportFieldDefinitions = (dataType) => FIELD_DEFINITIONS[dataType] || [];

export const buildSuggestedColumnMapping = (dataType, importSource, headers = []) => getImportFieldDefinitions(dataType)
  .reduce((mapping, field) => {
    mapping[field.key] = findMatchingHeader(headers, getAliasCandidates(dataType, importSource, field));
    return mapping;
  }, {});

export const getMissingRequiredFields = (dataType, columnMapping = {}) => getImportFieldDefinitions(dataType)
  .filter((field) => field.required && !columnMapping[field.key])
  .map((field) => field.label);

export const normalizeTradeRows = (dataType, rows, columnMapping = {}) => rows.map((row) => getImportFieldDefinitions(dataType)
  .reduce((normalized, field) => {
    const selectedHeader = columnMapping[field.key];
    if (selectedHeader && Object.prototype.hasOwnProperty.call(row, selectedHeader)) {
      normalized[field.key] = sanitizeCell(row[selectedHeader]);
    }
    return normalized;
  }, {}));

export const transformTradeRows = (dataType, rows) => rows
  .map((row) => getImportFieldDefinitions(dataType).reduce((transformed, field) => {
    if (Object.prototype.hasOwnProperty.call(row, field.key)) {
      transformed[field.key] = transformValue(field.type, row[field.key]);
    }
    return transformed;
  }, {}))
  .filter((row) => Object.values(row).some((value) => value !== null && !isBlank(value)));

const normalizeDeliveryExchange = (value) => {
  const normalized = normalizeComparable(value);
  if (!normalized) {
    return '';
  }
  if (normalized === 'nfo') {
    return 'nse';
  }
  if (normalized === 'bfo') {
    return 'bse';
  }
  return normalized;
};

const normalizeDeliverySegment = (value) => {
  const normalized = normalizeComparable(value);
  if (!normalized) {
    return 'cash';
  }
  if (['eq', 'cm', 'cash', 'series', 'nse', 'bse'].includes(normalized)) {
    return 'cash';
  }
  return normalized;
};

const buildDeliveryInstrumentIdentity = (row = {}) => {
  if (!row || typeof row !== 'object') {
    return '';
  }

  if (!isBlank(row.instrumentId)) {
    return `instrument:${normalizeComparable(row.instrumentId)}`;
  }
  if (!isBlank(row.angelToken)) {
    return `angel:${normalizeDeliveryExchange(row.exchange)}:${normalizeDeliverySegment(row.exchangeSegment)}:${normalizeComparable(row.angelToken)}`;
  }
  if (!isBlank(row.isin)) {
    return `isin:${normalizeComparable(row.isin)}`;
  }
  if (!isBlank(row.bseScripCode)) {
    return `bse:${normalizeComparable(row.bseScripCode)}`;
  }
  if (!isBlank(row.tradingSymbol)) {
    return `symbol:${normalizeDeliveryExchange(row.exchange)}:${normalizeDeliverySegment(row.exchangeSegment)}:${normalizeComparable(row.tradingSymbol)}`;
  }
  if (!isBlank(row.scripName)) {
    return `name:${normalizeDeliveryExchange(row.exchange)}:${normalizeDeliverySegment(row.exchangeSegment)}:${normalizeComparable(row.scripName)}`;
  }
  return '';
};

const buildDeliveryDuplicateKey = (clientId, row = {}) => {
  const instrumentIdentity = buildDeliveryInstrumentIdentity(row);
  const buyDate = String(row.buyDate ?? '').trim();
  const sellDate = String(row.sellDate ?? '').trim();
  const quantity = String(row.quantity ?? '').trim();

  if (!instrumentIdentity || !buyDate || !sellDate || !quantity) {
    return '';
  }

  return [normalizeComparable(clientId), instrumentIdentity, quantity, buyDate, sellDate].join('|');
};

export const findDuplicateDeliveryRows = (clientId, rows = []) => {
  const groupedRows = new Map();

  rows.forEach((row, index) => {
    const duplicateKey = buildDeliveryDuplicateKey(clientId, row);
    if (!duplicateKey) {
      return;
    }

    if (!groupedRows.has(duplicateKey)) {
      groupedRows.set(duplicateKey, {
        key: duplicateKey,
        label: row.tradingSymbol || row.scripName || row.isin || row.instrumentId || 'delivery trade',
        rowNumbers: [],
      });
    }

    groupedRows.get(duplicateKey).rowNumbers.push(index + 1);
  });

  return Array.from(groupedRows.values()).filter((entry) => entry.rowNumbers.length > 1);
};